import _ from 'lodash';
import moment from 'moment';
import * as Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser, useLocalize, useViewerRoles } from '@treams/treams-hooks';
import { CheckmarkIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Heading3, Modal, Popover, Separator, Spinner } from '@treams/treams-ui';

import { useJobTitleName } from '../../../../../job/useJobTitleName';
import LearningEventAttendee from '../components/LearningEventAttendee';
import LearningEventInviteAttendee from '../components/LearningEventInviteAttendee';

function LearningEventAttendeesModal(props: { event: graphql.LearningEvent }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const jobTitleName = useJobTitleName();
	const history = useHistory();
	const viewer = useAuthenticatedUser();
	const roles = useViewerRoles();

	const canEdit =
		props.event.status !== graphql.LearningEventStatus.Cancelled &&
		(roles.isWorkspaceSuperuser ||
			roles.isLearningOfficer ||
			props.event.createdBy.id === viewer.id);

	const [url, setUrl] = useState<string | undefined>();
	const [isAttendeesCopied, setIsAttendeesCopied] = useState<boolean>(false);

	useEffect(() => {
		const attendeeTypeTranslation = {
			[graphql.LearningEventAttendeeType.HostOrganizer]: t(
				'learning.event.modal.host.type.hostOrganizer',
			),
			[graphql.LearningEventAttendeeType.Host]: t('learning.event.modal.host.type.host'),
			[graphql.LearningEventAttendeeType.Organizer]: t('learning.event.modal.host.type.organizer'),
			[graphql.LearningEventAttendeeType.Attendee]: t('learning.event.modal.host.type.attendee'),
		};

		const attendeeTypeSortOrder = [
			graphql.LearningEventAttendeeType.HostOrganizer,
			graphql.LearningEventAttendeeType.Organizer,
			graphql.LearningEventAttendeeType.Host,
			graphql.LearningEventAttendeeType.Attendee,
		];

		const data = Papa.unparse([
			[
				t('learning.event.attendees.download.headers.firstName'),
				t('learning.event.attendees.download.headers.lastName'),
				t('learning.event.attendees.download.headers.email'),
				t('learning.event.attendees.download.headers.attendeeType'),
				t('learning.event.attendees.download.headers.jobTitles'),
			],
			..._.sortBy(
				[
					...props.event.hosts.edges.map(({ node, attendeeType }) => ({
						firstName: node.firstName,
						lastName: node.lastName,
						email: node.email,
						attendeeType,
						jobTitles: node.jobPositions.map(({ jobTitle }) => jobTitleName(jobTitle)).join(', '),
					})),
					...props.event.attendees.edges.map(({ node, attendeeType }) => ({
						firstName: node.firstName,
						lastName: node.lastName,
						email: node.email,
						attendeeType,
						jobTitles: node.jobPositions.map(({ jobTitle }) => jobTitleName(jobTitle)).join(', '),
					})),
				],
				[(row) => attendeeTypeSortOrder.indexOf(row.attendeeType), 'firstName', 'lastName'],
			).map((row) => [
				row.firstName,
				row.lastName,
				row.email,
				attendeeTypeTranslation[row.attendeeType],
				row.jobTitles,
			]),
		]);

		const blob = new Blob([data], { type: 'text/csv' });

		const urlObject = URL.createObjectURL(blob);

		setUrl(urlObject);

		return () => URL.revokeObjectURL(urlObject);
	}, [props.event.hosts.edges, props.event.attendees.edges]);

	return (
		<Popover center visible>
			<Modal>
				<Modal.Header
					title={t('learning.event.attendees.modal.title')}
					onClose={() => history.goBack()}
				/>

				<Modal.Contents>
					{props.event.hosts.edges.concat(props.event.attendees.edges).map((edge) => (
						<LearningEventAttendee
							regular
							key={edge.node.id}
							event={props.event}
							user={edge.node}
							type={edge.attendeeType}
							readonly={!canEdit}
						/>
					))}
					{canEdit && (
						<>
							<Box mt={3} mb={4}>
								<LearningEventInviteAttendee
									event={props.event}
									attendeeType={graphql.LearningEventAttendeeType.Attendee}
								/>
							</Box>

							<Box mb={2}>
								<Heading3>{t('learning.event.attendees.actions.title')}</Heading3>
							</Box>

							<Box mb={3}>
								<Separator />
							</Box>

							<Flex mt={3}>
								<Box mr={2}>
									{url && (
										<Button.Anchor
											type="secondary"
											href={url}
											small
											download={t('learning.event.attendees.download.filename', {
												eventName: localize(props.event.title).text,
												date: moment().format('YYYY-MM-DD'),
											})}
										>
											{t('learning.event.attendees.download.label')}
										</Button.Anchor>
									)}
								</Box>

								<Box>
									<Button
										small
										type="secondary"
										icon={isAttendeesCopied ? <CheckmarkIcon /> : undefined}
										onClick={() => {
											navigator.clipboard.writeText(
												props.event.attendees.edges.map(({ node }) => node.email).join('\n'),
											);

											setIsAttendeesCopied(true);
										}}
										title={
											isAttendeesCopied
												? t('learning.event.attendees.copied')
												: t('learning.event.attendees.copy')
										}
									/>
								</Box>
							</Flex>
						</>
					)}
				</Modal.Contents>
			</Modal>
		</Popover>
	);
}

export default function LearningEventAttendeesModalID(props: { eventID: string }) {
	const { data } = graphql.useLearningEventQuery({
		variables: {
			id: props.eventID,
		},
	});

	if (data) {
		return <LearningEventAttendeesModal event={data.learningEvent as graphql.LearningEvent} />;
	}

	return (
		<Popover center visible>
			<Modal>
				<Spinner centered />
			</Modal>
		</Popover>
	);
}
